import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ImageProvider = "gemini" | "openai";

const env = (k: string, fallback = "") =>
  (import.meta.env?.[k] as string | undefined)?.trim() || fallback;

/** Higgsfield usa par id:secret — armazenamos serializado em higgsfieldKey */
const higgsCombined = (() => {
  const single = env("VITE_HIGGSFIELD_API_KEY");
  if (single) return single;
  const id = env("VITE_HIGGSFIELD_API_ID");
  const secret = env("VITE_HIGGSFIELD_API_SECRET");
  return id && secret ? `${id}:${secret}` : "";
})();

const ENV_DEFAULTS = {
  anthropicKey:     env("VITE_ANTHROPIC_API_KEY"),
  geminiKey:        env("VITE_GEMINI_API_KEY"),
  openaiKey:        env("VITE_OPENAI_API_KEY"),
  higgsfieldKey:    higgsCombined,
  model:            env("VITE_ANTHROPIC_MODEL",  "claude-sonnet-4-6"),
  geminiImageModel: env("VITE_GEMINI_IMAGE_MODEL", "gemini-3-pro-image-preview"),
  openaiImageModel: env("VITE_OPENAI_IMAGE_MODEL", "gpt-image-1"),
  higgsfieldModel:  env("VITE_HIGGSFIELD_MODEL",   "seedance2")
};

interface SettingsState {
  anthropicKey: string;
  geminiKey: string;
  openaiKey: string;
  higgsfieldKey: string;
  model: string;                  // claude model
  imageProvider: ImageProvider;
  geminiImageModel: string;
  openaiImageModel: string;
  higgsfieldModel: string;
  setKey: (k: string) => void;
  setGeminiKey: (k: string) => void;
  setOpenaiKey: (k: string) => void;
  setHiggsfieldKey: (k: string) => void;
  setModel: (m: string) => void;
  setImageProvider: (p: ImageProvider) => void;
  setGeminiImageModel: (m: string) => void;
  setOpenaiImageModel: (m: string) => void;
  setHiggsfieldModel: (m: string) => void;
  clearKey: () => void;
}

export const useSettings = create<SettingsState>()(
  persist(
    (set) => ({
      anthropicKey: ENV_DEFAULTS.anthropicKey,
      geminiKey:    ENV_DEFAULTS.geminiKey,
      openaiKey:    ENV_DEFAULTS.openaiKey,
      higgsfieldKey: ENV_DEFAULTS.higgsfieldKey,
      model:        ENV_DEFAULTS.model,
      imageProvider: "gemini",
      geminiImageModel: ENV_DEFAULTS.geminiImageModel,
      openaiImageModel: ENV_DEFAULTS.openaiImageModel,
      higgsfieldModel:  ENV_DEFAULTS.higgsfieldModel,
      setKey:           (anthropicKey)   => set({ anthropicKey }),
      setGeminiKey:     (geminiKey)      => set({ geminiKey }),
      setOpenaiKey:     (openaiKey)      => set({ openaiKey }),
      setHiggsfieldKey: (higgsfieldKey)  => set({ higgsfieldKey }),
      setModel:         (model)          => set({ model }),
      setImageProvider: (imageProvider)  => set({ imageProvider }),
      setGeminiImageModel: (geminiImageModel) => set({ geminiImageModel }),
      setOpenaiImageModel: (openaiImageModel) => set({ openaiImageModel }),
      setHiggsfieldModel:  (higgsfieldModel)  => set({ higgsfieldModel }),
      clearKey:         ()               => set({ anthropicKey: "" })
    }),
    {
      name: "agencia-vpc-settings-v2",
      merge: (persisted: any, current: any) => {
        const out = { ...current, ...(persisted ?? {}) };
        (["anthropicKey","geminiKey","openaiKey","higgsfieldKey"] as const).forEach(k => {
          if (!out[k] && (ENV_DEFAULTS as any)[k]) out[k] = (ENV_DEFAULTS as any)[k];
        });
        // Sanitiza modelos antigos quebrados — força nomes válidos
        const validGemini = new Set([
          "gemini-3-pro-image-preview",
          "gemini-3.1-flash-image-preview",
          "gemini-2.5-flash-image",
          "gemini-2.5-flash-image-preview"  // legado, ainda funciona
        ]);
        if (!validGemini.has(out.geminiImageModel)) {
          out.geminiImageModel = "gemini-3-pro-image-preview";
        }
        const validOpenAI = new Set(["gpt-image-2", "gpt-image-1", "dall-e-3"]);
        if (!validOpenAI.has(out.openaiImageModel)) {
          out.openaiImageModel = "gpt-image-2";
        }
        return out;
      }
    }
  )
);
